import React from 'react';
import { Box, Heading, Text, Search } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import s from './HeaderBanner.module.scss';

interface HeaderBannerWrapperProps {
  titleKey: string;
  className?: string;
  subtitleKey: string;
  isMobileView?: boolean;
  dataHook?: string;
  children?: React.ReactElement | null;
}

interface HeaderBannerProps
  extends Omit<
    HeaderBannerWrapperProps,
    'className' | 'hasSearch' | 'isMobileView'
  > {
  containerClassName: string;
  textWrapperClassName?: string;
}

const getClasses = ({
  isCentered,
  isMobileView,
  className,
}: Pick<HeaderBannerWrapperProps, 'isMobileView' | 'className'> & {
  isCentered?: boolean;
}) => {
  const containerClassNames = [s.bannerWrapper];
  const textWrapperClassNames = [s.textWrapper];

  if (className) {
    containerClassNames.push(className);
  }

  if (isMobileView) {
    containerClassNames.push(s.mobileView);
    textWrapperClassNames.push(s.mobileView);
  }

  if (isCentered) {
    containerClassNames.push(s.centeredBannerContainer);
    textWrapperClassNames.push(s.centeredTextWrapper);
  }

  return {
    containerClassNames: containerClassNames.join(' '),
    textWrapperClassNames: textWrapperClassNames.join(' '),
  };
};

const HeaderBannerWrapper = ({
  className,
  isMobileView,
  children,
  ...commonProps
}: HeaderBannerWrapperProps) => {
  const { containerClassNames, textWrapperClassNames } = getClasses({
    isCentered: Boolean(children),
    isMobileView,
    className,
  });

  return (
    <HeaderBanner
      containerClassName={containerClassNames}
      textWrapperClassName={textWrapperClassNames}
      {...commonProps}
    >
      {children}
    </HeaderBanner>
  );
};

const HeaderBanner = ({
  titleKey,
  subtitleKey,
  containerClassName,
  textWrapperClassName,
  dataHook,
  children,
}: HeaderBannerProps) => {
  const { t } = useTranslation();

  return (
    <Box direction="vertical" className={containerClassName}>
      <Box direction="vertical" className={textWrapperClassName}>
        <Heading as="h1" dataHook={dataHook || ''}>
          <Text>{t(titleKey)}</Text>
        </Heading>
        <Text className={s.subtitle}>{t(subtitleKey)}</Text>
      </Box>
      {children}
    </Box>
  );
};

export default HeaderBannerWrapper;
